import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, ReactNode } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

export type CollapsiblePaneProps = Readonly<{
    /**
     * Pane title.
     */
    title: ReactNode;
    /**
     * Pane subtitle
     */
    subtitle?: ReactNode;
    /**
     * If the pane is expanded.
     */
    expanded: boolean;
    /**
     * Callback for when the pane is expanded or collapsed.
     */
    onChange: (isExpanded: boolean) => void;
    /**
     * Content to show when expanded.
     */
    children?: ReactNode;
    /**
     * When true, the children will always be rendered but be
     * hidden when the pane is not expanded (defaults to `false`).
     */
    alwaysRenderChildren?: boolean;
    /**
     * ARIA text label for this element.
     */
    'aria-label'?: string;
    /**
     * Optional classes to apply to the pane.
     */
    className?: string;
    /**
     * Removes the padding from the pane header.
     */
    noHeaderPadding?: boolean;
    /**
     * Removes the border from the pane header.
     */
    noBorder?: boolean;
    /**
     * The type of open/close icons to use (defaults to `plus`).
     */
    iconType?: 'plus' | 'chevron';
    /**
     * The type of vertical alignment to use in the title (default to `start`).
     */
    titleAlign?: 'start' | 'center';
}>;

const titleIcons = {
    chevron: { collapsed: faAngleDown, expanded: faAngleUp },
    plus: { collapsed: faPlus, expanded: faTimes },
};

const PaneContainer = styled.div<{ $noBorder: boolean }>`
    border: ${({ $noBorder }) => ($noBorder ? 'none' : `solid 1px ${tokens.border.default}`)};
`;

const PaneHeader = styled.div<{ $noHeaderPadding: boolean; $hasSubtitle: boolean }>`
    display: grid;
    grid-template-columns: 1fr auto;
    ${({ $hasSubtitle }) => ($hasSubtitle ? 'grid-template-rows: 1fr auto;' : '')}
    column-gap: 1rem;
    color: ${tokens.text.primary};
    padding: ${({ $noHeaderPadding }) => ($noHeaderPadding ? 0 : '1rem')};
    font-size: 1.4rem;
    cursor: pointer;

    & .grid-title {
        grid-row: 1 / 2;
    }

    & .grid-icon {
        grid-row: 1 / 3;
        grid-column: 2 / 3;

        display: flex;
        align-items: center;
        justify-content: center;
    }

    & .grid-subtitle {
        grid-row: 2 / 3;
    }
`;

const Subtitle = styled.div`
    color: ${tokens.text.secondary};
`;

/**
 * Component for rendering a pane in which the content can be collapsed.
 */
const CollapsiblePane = forwardRef<HTMLDivElement, CollapsiblePaneProps>(
    (
        {
            title,
            subtitle,
            expanded,
            onChange,
            children,
            noHeaderPadding = false,
            noBorder = false,
            alwaysRenderChildren = false,
            'aria-label': ariaLabel,
            className,
            iconType = 'plus',
            titleAlign = 'top',
        },
        ref,
    ) => (
        <PaneContainer
            ref={ref}
            aria-label={ariaLabel}
            className={className}
            $noBorder={noBorder}
        >
            <PaneHeader
                $noHeaderPadding={noHeaderPadding}
                $hasSubtitle={!!subtitle}
                tabIndex={0}
                role="button"
                className={`align-${titleAlign}`}
                onClick={() => onChange(!expanded)}
                onKeyPress={() => onChange(!expanded)}
            >
                <div className="flex-grow-1 text-left bold grid-title">{title}</div>
                <div className="text-right px-2 grid-icon">
                    <FontAwesomeIcon
                        icon={
                            expanded
                                ? titleIcons[iconType].expanded
                                : titleIcons[iconType].collapsed
                        }
                    />
                </div>
                {subtitle && (
                    <Subtitle className="ellipsis-1-line grid-subtitle">
                        {subtitle}
                    </Subtitle>
                )}
            </PaneHeader>
            <div className="f-14" style={{ display: expanded ? 'block' : 'none' }}>
                {(expanded || alwaysRenderChildren) && children}
            </div>
        </PaneContainer>
    ),
);

export { CollapsiblePane };
